import React from "react";
import { array } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { TCaseConflictOfInterestForm } from "../../../../../domain/codecs/form/CaseConflictsOfInterestForms";
import { TCoiPartySide } from "../../../../../domain/codecs/CaseConflictsOfInterest";
import { snakeCaseToCopyText } from "../../../../../shared/src/util";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Padding } from "../../BuildingBlocks/Padding";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../WeightBold/WeightBold";
import { CRMLink } from "../../CRMLink/CRMLink";
import { CRMIcon } from "../../CRMIcon/CRMIcon";

const COLUMN_SIZES = "1.4fr 1.4fr 1fr 0.9fr 1fr";

const sideLabel = (side: TCoiPartySide): string =>
    side === "ours" ? "our client" : side === "theirs" ? "other side" : "the property";

const isOpposing = (coi: TCaseConflictOfInterestForm): boolean =>
    coi.children.party_side !== "neutral"
    && coi.children.flagged_against_party_side !== "neutral"
    && coi.children.party_side !== coi.children.flagged_against_party_side;

/**
 * description: The review list for a case's conflict-of-interest matches, with both parties
 * named and the side each sits on, so a decision can be made without opening the other case.
 *
 * **When to use:** On the case Conflicts of Interest tab, in place of a one-match-at-a-time card.
 *
 * @example
 * <CRMConflictOfInterestTable
 *     conflictsOfInterest={forms}
 *     caseId={caseId}
 *     otherCaseHref={(id) => `/crm/legal/cases/${id}`}
 *     onSelect={setSelected}
 *     selectedId={selectedId}
 * />
 */
export const CRMConflictOfInterestTable = (props: {
    conflictsOfInterest: Array<TCaseConflictOfInterestForm>;
    otherCaseHref: (caseId: string) => string;
    onSelect: (coi: TCaseConflictOfInterestForm) => void;
    selectedId: string | null;
}): JSX.Element => {
    const needsAttention = pipe(
        props.conflictsOfInterest,
        array.filter((coi) => coi.children.auto_cleared_rule === null),
    );
    const settledByRule = pipe(
        props.conflictsOfInterest,
        array.filter((coi) => coi.children.auto_cleared_rule !== null),
    );

    return (
        <SpacingColumn spacing={CRMSpacing.MEDIUM}>
            <SpacingRow spacing={CRMSpacing.SMALL} childSize={COLUMN_SIZES}>
                <CRMParagraph><WeightBold>Who</WeightBold></CRMParagraph>
                <CRMParagraph><WeightBold>Against</WeightBold></CRMParagraph>
                <CRMParagraph><WeightBold>What matched</WeightBold></CRMParagraph>
                <CRMParagraph><WeightBold>Other matter</WeightBold></CRMParagraph>
                <CRMParagraph><WeightBold>Status</WeightBold></CRMParagraph>
            </SpacingRow>

            {needsAttention.length === 0 && settledByRule.length > 0 &&
                <CRMParagraph>Nothing here needs a decision.</CRMParagraph>
            }

            {pipe(
                needsAttention,
                array.map((coi) => (
                    <div
                        key={coi.children.id}
                        onClick={() => props.onSelect(coi)}
                        className={`
                            crm-conflict-of-interest-table__row
                            ${isOpposing(coi) ? "crm-conflict-of-interest-table__row--opposing" : ""}
                            ${coi.children.id === props.selectedId ? "crm-conflict-of-interest-table__row--selected" : ""}
                        `}
                    >
                        <Padding type="all" spacing={CRMSpacing.SMALL}>
                            <SpacingRow spacing={CRMSpacing.SMALL} childSize={COLUMN_SIZES}>
                                <CRMParagraph>
                                    <WeightBold>{coi.children.party_name || "—"}</WeightBold>
                                    {` (${sideLabel(coi.children.party_side)})`}
                                </CRMParagraph>
                                <CRMParagraph>
                                    <WeightBold>{coi.children.flagged_against_party_name || "—"}</WeightBold>
                                    {` (${sideLabel(coi.children.flagged_against_party_side)})`}
                                </CRMParagraph>
                                <CRMParagraph>
                                    {snakeCaseToCopyText(coi.children.source)}
                                    {`: ${coi.children.data_at_time_of_check}`}
                                </CRMParagraph>
                                <CRMParagraph>
                                    <CRMLink
                                        target="_blank"
                                        href={props.otherCaseHref(coi.children.flagged_against_case_id)}
                                        linkStyle="normal"
                                    >
                                        {coi.children.flagged_against_case_is_live ? "Open matter" : "Closed matter"}
                                    </CRMLink>
                                </CRMParagraph>
                                <CRMParagraph>
                                    {coi.edited.status === "flagged" && isOpposing(coi) &&
                                        <SpacingRow spacing={CRMSpacing.TINY}>
                                            <CRMIcon iconName="alert" size="tiny" colour="highlights-instructional-brick-8" />
                                            <span>Opposing sides</span>
                                        </SpacingRow>
                                    }
                                    {coi.edited.status === "flagged" && !isOpposing(coi) && <span>Needs a decision</span>}
                                    {coi.edited.status !== "flagged" && <span>{snakeCaseToCopyText(coi.edited.status)}</span>}
                                </CRMParagraph>
                            </SpacingRow>
                        </Padding>
                    </div>
                )),
            )}

            {settledByRule.length > 0 &&
                <div className="crm-conflict-of-interest-table__settled-by-rule">
                <Padding type="all" spacing={CRMSpacing.SMALL}>
                    <CRMParagraph>
                        <WeightBold>{`${settledByRule.length} routine ${settledByRule.length === 1 ? "match" : "matches"} settled by rule.`}</WeightBold>
                        {" These were the same client on both matters, or a matter that has already closed. Each one records why."}
                    </CRMParagraph>
                </Padding>
                </div>
            }
        </SpacingColumn>
    );
};
